import { makeStyles } from "tss-react/mui"
import { Box, Stack, Typography } from "@mui/material"

import { Attendees } from "../../../../shared/components/dialogs/receiptConfirmOrderDialog/components/Attendees"
import { OrderSummary } from "../../../../shared/components/dialogs/receiptConfirmOrderDialog/components/OrderSummary"
import { Resources } from "../../../../shared/components/dialogs/receiptConfirmOrderDialog/components/Resources"
import { tailwindPalette } from "../../../theme/tailwindPalette"
import { Details } from "../../../../shared/components/dialogs/receiptConfirmOrderDialog/components/Details"
import { DynamicConfigKey } from "../../../../shared/components/createOrder/components/form/formTypes"
import { useDateTime } from "../../../localization/useDateTime"
import { BillReceiver } from "./components/BillReceiver"
import { RepeatOrder } from "./components/RepeatOrder"
import { MeetingServiceOrderResource } from "../../../merchant/accounting/accounting/accountingTypes"
import { MeetingDetailsOrder } from "../home/meetingServiceApi"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { ErrorBoundary } from "react-error-boundary"
import { DeliveryNote } from "../../../../shared/components/dialogs/receiptConfirmOrderDialog/components/DeliveryNote"
import { getFirstOrderNote } from "../../../../shared/components/createOrder/components/drawer/deliveryNote/helpers"
import { getOrderPerson } from "../../../../shared/utils/getOrderPerson"
import { SimpleStatusChip } from "../../../merchant/merchant/production/meetingService/components/StatusMenu"

type StylesProps = {
    isFirst: boolean
}
const useStyles = makeStyles<StylesProps>()((theme, { isFirst }) => ({
    slideContainer: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
        marginTop: isFirst ? "1.5rem" : 0,
        "@media print": {
            flex: "none",
            display: "block",
            backgroundColor: "transparent",
            padding: 0,
            margin: "0 auto",
            width: "100%",
            maxWidth: "190mm",
            boxSizing: "border-box",
            overflow: "hidden",
            pageBreakInside: "avoid",
            pageBreakBefore: isFirst ? "avoid" : "always",
            "&:last-child": {
                pageBreakAfter: "avoid",
            },
        },
        [theme.breakpoints.down("sm")]: {
            marginTop: "0 !important",
        },
    },
    slide: {
        width: "80%",
        padding: "3.5rem",
        backgroundColor: "white",
        boxShadow: "0px 0px 10px 0px #00000026;",
        "@media print": {
            width: "100%",
            maxWidth: "210mm",
            padding: "1rem",
            boxShadow: "none",
            backgroundColor: "white",
            margin: "0 auto",
            pageBreakInside: "avoid",
            pageBreakAfter: "avoid",
        },
        [theme.breakpoints.down("sm")]: {
            boxShadow: "none",
            width: "100%",
            padding: 0,
            gap: "2rem",
        },
    },
    meetingInfo: {
        maxWidth: "70%",
        gap: "1.5rem",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: {
            marginTop: "2rem",
            maxWidth: "100%",
        },
    },
    meetingInfoNumber: {
        verticalAlign: "middle",
        color: tailwindPalette.gray[500],
        fontSize: "1.5rem",
        lineHeight: 1.3,
    },
    meetingInfoContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "baseline",
            padding: "0 1rem",
        },
    },
    errorTitle: {
        fontWeight: 600,
    },
    hidePrint: {
        "@media print": {
            display: "none",
        },
    },
    mockPrintHeight: {
        "@media print": {
            height: "40rem",
        },
    },
}))

function mapBookingResources(src?: MeetingServiceOrderResource[]) {
    const list = src ?? []
    return list.map((el) => ({
        name: el.name,
        ...(el.room?.tableSetup ? { tableSetups: [{ name: el.room.tableSetup.name }] } : {}),
    }))
}

type ReceiptDetailsProps = {
    orderDetails: MeetingDetailsOrder
    isFirst: boolean
}

export const ReceiptDetails = ({ orderDetails, isFirst }: ReceiptDetailsProps) => {
    const dateTimeFactory = useDateTime()

    const resourcesOut = mapBookingResources(orderDetails?.bookingDetails?.resources)
    const statusDetails = orderDetails.deliveryDetails?.statusDetails
    const { classes } = useStyles({ isFirst })
    const orderErrorDetails = orderDetails?.serviceResponse?.displayMessage

    const ErrorFallback = ({ isClientError }: { isClientError?: boolean }) => (
        <Box id="printable-content-hidden" className={classes.slideContainer}>
            <Stack direction="column" spacing="1.5rem" className={classes.slide}>
                {isClientError && (
                    <LocalizedStrict
                        id="confirm-order-dialog-order-error"
                        vars={{ orderId: orderDetails?.id.toString() }}
                    >
                        <Typography variant="h5" className={classes.errorTitle}>
                            Failed to load packing slip with id #{orderDetails?.id.toString()}
                        </Typography>
                    </LocalizedStrict>
                )}
                {orderErrorDetails ? (
                    <Typography variant="body1">{orderErrorDetails}</Typography>
                ) : (
                    <LocalizedStrict id="confirm-order-dialog-order-error-description">
                        <Typography variant="body1">
                            This order could not be processed due to missing or invalid data.
                        </Typography>
                    </LocalizedStrict>
                )}
            </Stack>
            <Box className={classes.mockPrintHeight} />
        </Box>
    )

    const { creator, person } = getOrderPerson(orderDetails?.organizers)

    return orderDetails.serviceResponse ? (
        <ErrorFallback />
    ) : (
        <ErrorBoundary FallbackComponent={() => <ErrorFallback isClientError />}>
            <Box id="printable-content" className={classes.slideContainer}>
                <Stack direction="column" spacing="2.5rem" className={classes.slide}>
                    <Stack className={classes.meetingInfoContainer}>
                        <Box className={classes.meetingInfo}>
                            <Typography variant="h4" fontSize="2.5rem" fontWeight={500}>
                                {orderDetails?.meetingDetails?.meetingName}{" "}
                                <Typography className={classes.meetingInfoNumber} component="span">
                                    #{orderDetails?.id}
                                </Typography>
                            </Typography>
                        </Box>
                        {statusDetails?.status && (
                            <SimpleStatusChip
                                status={statusDetails.status}
                                tooltipLabel={statusDetails.message ?? ""}
                            />
                        )}
                    </Stack>
                    <Stack gap="1.5rem">
                        {orderDetails && (
                            <Details
                                orderDetails={{
                                    event: {
                                        start: orderDetails.meetingDetails.startDateTime,
                                        end: orderDetails.meetingDetails.endDateTime,
                                    },
                                    priceList: {
                                        merchantName: orderDetails?.["sellerSupplierParty"]?.name,
                                    },
                                    [DynamicConfigKey.MEETING_RESPONSIBLE]: person,
                                    [DynamicConfigKey.DELIVERY_TYPE]: orderDetails.deliveryDetails?.type,
                                    [DynamicConfigKey.NUMBER_OF_SEATS]: orderDetails.deliveryDetails?.sittings,
                                    [DynamicConfigKey.OTHER_DELIVERY_LOCATION]:
                                        orderDetails.deliveryDetails?.location.name,
                                    [DynamicConfigKey.MULTIPLE_DELIVERIES]: {
                                        deliveryDate: orderDetails.deliveryDetails?.time,
                                    },
                                    created: orderDetails.created,
                                    updated: orderDetails.updated,
                                }}
                                mainUser={{
                                    displayName: creator?.name ?? "",
                                    email: creator?.email ?? "",
                                }}
                                resourceNames={resourcesOut.map((el) => el.name).join(", ")}
                            />
                        )}
                        {orderDetails.meetingDetails?.recurrence && (
                            <RepeatOrder recurrence={orderDetails.meetingDetails?.recurrence} />
                        )}
                        {orderDetails?.buyerCustomerParty && (
                            <BillReceiver
                                billReceiver={orderDetails.buyerCustomerParty}
                                costCodes={orderDetails.accountingDetails.costCodes}
                            />
                        )}
                        {!!orderDetails.meetingDetails?.attendees?.length && (
                            <Attendees list={orderDetails?.meetingDetails.attendees} />
                        )}
                    </Stack>
                    <Stack gap="2rem">
                        <Stack gap="1.25rem">
                            {!!orderDetails.deliveries?.length && (
                                <>
                                    <OrderSummary
                                        isRefundOrder={false}
                                        deliveryDetails={{
                                            deliveryDate: orderDetails.deliveries?.[0]?.deliveryTime,
                                            activeTime: null,
                                            deliveryTimes: orderDetails.deliveries?.map((item) => ({
                                                time: dateTimeFactory.toHourMinute(
                                                    dateTimeFactory.fromApi(item.deliveryTime),
                                                ),
                                                orderLines: item.orderLines.map((item) => ({
                                                    id: item.id ?? 0,
                                                    name: item.name,
                                                    items: item.items,
                                                    price: item.itemPrice,
                                                    productNumber: "",
                                                    quantity: item.items,
                                                    productId: item.productId,
                                                    isActive: true,
                                                    buyerParty: orderDetails.accountingCustomerParty?.name ?? "",
                                                })),
                                            })),
                                        }}
                                        priceListIncludeVat={!!orderDetails.priceList?.includeVat}
                                    />
                                    <DeliveryNote text={getFirstOrderNote(orderDetails.deliveries)} />
                                </>
                            )}
                        </Stack>
                        {resourcesOut?.length > 0 && (
                            <Resources
                                defaultExpanded={!orderDetails?.orderSummary?.hasCatering}
                                resources={resourcesOut}
                                event={{
                                    start: orderDetails.meetingDetails.startDateTime,
                                    end: orderDetails.meetingDetails.endDateTime,
                                }}
                            />
                        )}
                    </Stack>
                </Stack>
            </Box>
        </ErrorBoundary>
    )
}
